import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import { BusinessTransactionType } from '../../../foundation/models/enums/business-transaction-type.enum';
import { SourceSystemType } from '../../../foundation/models/enums/source-system-type.enum';
import { WIPStatusType } from '../../../wip/models/enums/w-i-p-status-type.enum';
import { RouteDefinitionType } from '../../enums/route-definition.enum';
import { RouteParameterType } from '../../enums/route-parameters.enum';
import { RoutePartType } from '../../enums/route-part.enum';
import { BusinessTransactionMetaData } from '../../models/business-transaction/business-transaction-metadata.model';
import { AuxRoute } from '../../models/route/aux-route.model';
import { RouteConfiguration } from '../../models/route/route-configuration.model';
import { RouteDefaults } from '../../models/route/route-defaults.model';
import { BusinessTransactionService } from '../../services/business-transaction/business-transaction.service';
import { RouteBuilderFactory } from '../route-builder/route-builder-factory.service';
import { RouteConfigurationBaseService } from './route-configuration-base.service';

@Injectable()
/**
 * Service for creating transaction route configuration
 */
export class TransactionRouteConfigurationService extends RouteConfigurationBaseService {

    private _routeConfigurations: RouteConfiguration[];
    private _businessTransactionType: BusinessTransactionType = new BusinessTransactionType();
    private wipStatus: WIPStatusType = new WIPStatusType();
    private sourceSystemType: SourceSystemType = new SourceSystemType();

    constructor(
        public _businessTransactionService: BusinessTransactionService,
        private routeBuilderFactory: RouteBuilderFactory
    ) {
        super();
    }

    /**
     * All available transaction route confiurations
     */
    public get routeConfigurations(): RouteConfiguration[] {
        if (this._routeConfigurations == undefined) {
            this._routeConfigurations = [];

            this._routeConfigurations.push(this.getTransactionDefaultRouteConfiguration());

            this._routeConfigurations.push(this.getTransactionConfirmationRouteConfiguration());

            this._routeConfigurations.push(this.getTransactionFeesRouteConfiguration());

            this._routeConfigurations.push(this.getDriverTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getPaceTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getCaseDefaultRouteConfiguration());

            this._routeConfigurations.push(this.getTNRTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getFinanceTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getSpinnerlayoutRouteConfiguration());

            this._routeConfigurations.push(this.getCollectionsTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getIRPTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getCommercialPermittingTransactionFinalizationRouteConfiguration());

            this._routeConfigurations.push(this.getAircraftTransactionFinalizationRouteConfiguration());

        }
        return this._routeConfigurations;
    }

    /**
     * Configuration for Transaction Fees
     */
    private getTransactionFeesRouteConfiguration(): RouteConfiguration {

        // BEGIN TRANSACTION FEES/TAXES
        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.TransactionFeesTaxes;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeName = 'fees-taxes';
        body.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'fees-taxes-header';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        // END TRANSACTION FEES/TAXES
        return newRouteConfiguration;

    }

    /**
     * Configuration for Transaction Confirmation
     */
    private getTransactionConfirmationRouteConfiguration(): RouteConfiguration {

        // BEGIN TRANSACTION CONFIRMATION
        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.TransactionConfirmation;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeName = 'confirmation';
        body.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'summary';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        // END TRANSACTION CONFIRMATION
        return newRouteConfiguration;

    }

    /**
     * Default Configuration for Transaction
     */
    private getTransactionDefaultRouteConfiguration(): RouteConfiguration {

        // BEGIN TRANSACTION DEFAULT
        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.TransactionDefault;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'body';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'tabs';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     *Finalization Configuration for IRPTransaction
     */
    private getIRPTransactionFinalizationRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.IRPFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-body';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     *Finalization Configuration for FinanceTransaction
     */
    private getFinanceTransactionFinalizationRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.FinanceFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-body';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     *Finalization Configuration for FinanceTransaction
     */
    private getCollectionsTransactionFinalizationRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.CollectionsFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-body';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     *Finalization Configuration for DriverTransaction
     */
    private getDriverTransactionFinalizationRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.DriverFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-body';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
   *Finalization Configuration for T&RTransaction
   */
    private getTNRTransactionFinalizationRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.TnrFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-layout';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     *Finalization Configuration for Pace Transaction
     */
    private getPaceTransactionFinalizationRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.PaceFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-body';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     * Spinner Layout during Awaiting postpayment status
     */
    private getSpinnerlayoutRouteConfiguration(): RouteConfiguration {

        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.SpinnerLayout;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'spinner-layout';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'spinner-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    private getCommercialPermittingTransactionFinalizationRouteConfiguration(): RouteConfiguration {
        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.CommercialPermittingFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-layout';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    private getAircraftTransactionFinalizationRouteConfiguration(): RouteConfiguration {
        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.AircraftFinalization;
        newRouteConfiguration.routes = [];

        const body = new AuxRoute();
        body.outletName = RoutePartType.body;
        body.isModule = true;
        body.routeParameter = [RouteParameterType.id];
        body.routeName = 'finalize-layout';

        newRouteConfiguration.routes.push(body);

        const head = new AuxRoute();
        head.outletName = RoutePartType.head;
        head.isModule = true;
        head.routeParameter = [RouteParameterType.id];
        head.routeName = 'finalize-head';

        newRouteConfiguration.routes.push(head);

        const sidebar = new AuxRoute();
        sidebar.outletName = RoutePartType.sidebar;
        sidebar.isModule = true;
        sidebar.routeParameter = [RouteParameterType.id];
        sidebar.routeName = 'service-queue';

        newRouteConfiguration.routes.push(sidebar);

        const primary = new AuxRoute();
        primary.outletName = RoutePartType.primary;
        primary.isModule = true;
        primary.routeParameter = [RouteParameterType.id];

        newRouteConfiguration.routes.push(primary);

        return newRouteConfiguration;
    }

    /**
     * Finalization Configuration for Pace Transaction
     */
    private getCaseDefaultRouteConfiguration() {
        const newRouteConfiguration = new RouteConfiguration();
        newRouteConfiguration.name = RouteDefinitionType.CaseDefault;
        newRouteConfiguration.routes = [];
        newRouteConfiguration.isNonAuxRoute = true;

        return newRouteConfiguration;
    }

    /**
     * returns the defaul configuration based on the metadata passed in
     */
    public getDefaultRouteConfiguration(metadata): string {
        let retVal: string = RouteDefinitionType.TransactionDefault;

        const businessTransactionMetadata: BusinessTransactionMetaData = metadata;

        // Spinner Layout during POstPayment Step based on 'Awaiting Postpayment' status
        if (businessTransactionMetadata.wipStatusTypeId === this.wipStatus.AwaitingPostPayment.id || businessTransactionMetadata.wipStatusTypeId === this.wipStatus.PaymentComplete.id) {
            retVal = RouteDefinitionType.SpinnerLayout;
        }

        // Finalization View for Drivers
        if (+businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.Driver.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.DriverFinalization;
        }

        // Finalization View for Finance
        if (+businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.Finance.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.FinanceFinalization;
        }

        // Finalization View for T&R
        if (businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.TitleRegistration.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.TnrFinalization;
        }

        // Finalization View for IRP
        if (businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.IRP.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.IRPFinalization;
        }

        // Finalization View for IRP
        if (businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.CommercialPermitting.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.CommercialPermittingFinalization;
        }

        // Finalization View for Aircraft
        if (businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.Aircraft.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.AircraftFinalization;
        }

        // For some T&R transactions that are not finalized
        if (+businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.TitleRegistration.id && (businessTransactionMetadata.wipStatusTypeId !== this.wipStatus.Finalized.id && businessTransactionMetadata.wipStatusTypeId !== this.wipStatus.AwaitingPostFinalize.id && businessTransactionMetadata.wipStatusTypeId !== this.wipStatus.ServiceComplete.id)) {
            if (+businessTransactionMetadata.businessTransactionTypeId === this._businessTransactionType.IssueGFDecal.id) {
                retVal = RouteDefinitionType.TransactionConfirmation;
            }
        }

        // Finalization View for Pace
        if (+businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.PACE.id && (businessTransactionMetadata.wipStatusTypeId == this.wipStatus.Finalized.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id || businessTransactionMetadata.wipStatusTypeId == this.wipStatus.ServiceComplete.id)) {
            retVal = RouteDefinitionType.PaceFinalization;
        }

        if (+businessTransactionMetadata.sourceSystemTypeId === this.sourceSystemType.Case.id && (businessTransactionMetadata.wipStatusTypeId === this.wipStatus.AwaitingPostPayment.id || businessTransactionMetadata.wipStatusTypeId === this.wipStatus.PaymentComplete.id)) {
            retVal = RouteDefinitionType.CaseDefault;
        }

        if (this.isCaseATPTransaction(businessTransactionMetadata)) {
            retVal = RouteDefinitionType.CaseDefault;
        }

        // make a decision on what the default route configuration name is

        return retVal;
    }

    /**
     * Gets a configuration by name
     */
    public getRouteConfiguration(routeDefinitionType): RouteConfiguration {

        const routeConfiguration = this.routeConfigurations.find(c => c.name == routeDefinitionType);

        return routeConfiguration;
    }

    /**
     * gets transaction routing defaults
     */
    public getRouteDefaults(sourceSystemTypeId, businessTransactionTypeId, wipStatusTypeId): RouteDefaults {

        const defaultRouteName: RouteDefaults = new RouteDefaults();

        if (wipStatusTypeId != this.wipStatus.PaymentComplete.id && wipStatusTypeId != this.wipStatus.AwaitingPostPayment.id && wipStatusTypeId != this.wipStatus.Finalized.id && wipStatusTypeId != this.wipStatus.ServiceComplete.id && wipStatusTypeId != this.wipStatus.AwaitingPostFinalize.id) {
            let routeBuilder = this.routeBuilderFactory.create(+sourceSystemTypeId);
            let defaultRoute = routeBuilder.getRouteList(+businessTransactionTypeId);

            if (defaultRoute != undefined && defaultRoute.length > 0) {
                defaultRouteName.routeName = defaultRoute[0].name;
            }

            return defaultRouteName;
        } else if (wipStatusTypeId == this.wipStatus.PaymentComplete.id || wipStatusTypeId == this.wipStatus.AwaitingPostPayment.id || wipStatusTypeId == this.wipStatus.Finalized.id || wipStatusTypeId == this.wipStatus.AwaitingPostFinalize.id) {
            defaultRouteName.routeName = 'spinner-layout';
            return defaultRouteName;
        } else if (wipStatusTypeId == this.wipStatus.ServiceComplete.id) {
            defaultRouteName.routeName = 'finalize-layout';
            return defaultRouteName;
        }
    }

    /**
     * gets transaction route parameters
     */
    public async getRouteParameters(businessTransactionNumberId: string) {

        const params = {};
        try {
            const metadata = await this._businessTransactionService.getTransactionMetaData(businessTransactionNumberId).pipe(first()).toPromise();
            if (metadata != undefined) {

                if (metadata.sourceSystemTypeId === this.sourceSystemType.Case.id || this.isCaseATPTransaction(metadata)) {
                    const itemKey = metadata.itemKeyCollection.filter(x => {
                        if (x.businessTransactionItemKeyTypeId === 10) {
                            return x.itemKeyId;
                        }
                    });
                    params[RouteParameterType.id] = +itemKey[0].itemKeyId;
                    metadata.sourceSystemTypeId = new SourceSystemType().Case.id;
                } else {
                    params[RouteParameterType.id] = businessTransactionNumberId;
                }

                params[RouteParameterType.agentId] = metadata.agentId;
                params[RouteParameterType.transactionTypeId] = metadata.businessTransactionTypeId;
                params[RouteParameterType.sourceSystemTypeId] = metadata.sourceSystemTypeId;
            }
        } catch (e) {

        }
        return params;
    }

    private isCaseATPTransaction(businessTransactionMetadata): boolean {

        if (+businessTransactionMetadata.sourceSystemTypeId != this.sourceSystemType.Case.id &&
            +businessTransactionMetadata.businessTransactionTypeId === new BusinessTransactionType().MiscellaneousPurchase.id &&
            +businessTransactionMetadata.itemKeyCollection.some(x => x.businessTransactionItemKeyTypeId === 10) &&
            (businessTransactionMetadata.wipStatusTypeId === this.wipStatus.AwaitingPostPayment.id || businessTransactionMetadata.wipStatusTypeId === this.wipStatus.PaymentComplete.id)) {
            return true;
        }
        return false;
    }
}
